import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { BOARD_MANUAL_PATH } from '../constants/routes';
import { DEFAULT_MANUAL_GROUP_NAME, type ManualGroupsTreeResponse } from '../constants/manual';

type GroupRow = ManualGroupsTreeResponse['groups'][number];

type SortableRowProps = {
  group: GroupRow;
  disabled: boolean;
  onEdit: (g: GroupRow) => void;
  onDelete: (g: GroupRow) => void;
};

/** ドラッグハンドル付きの1行 */
const SortableGroupRow: React.FC<SortableRowProps> = ({ group, disabled, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `group-${group.id}`,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    ...(isDragging ? { zIndex: 1, position: 'relative' as const } : {}),
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 44, py: 0.5, pr: 0 }}>
        <IconButton
          size="small"
          aria-label="並び替え"
          disabled={disabled}
          sx={{ cursor: disabled ? 'default' : 'grab', touchAction: 'none' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" color={disabled ? 'disabled' : 'action'} />
        </IconButton>
      </TableCell>
      <TableCell>{group.name}</TableCell>
      <TableCell align="right">
        <IconButton size="small" aria-label="編集" onClick={() => onEdit(group)} disabled={disabled}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="削除"
          color="error"
          disabled={disabled || group.name === DEFAULT_MANUAL_GROUP_NAME}
          onClick={() => onDelete(group)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const ManualGroupsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GroupRow | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [sortInput, setSortInput] = useState('0');
  const [saving, setSaving] = useState(false);

  /** グループ削除確認ダイアログで表示する対象 */
  const [deleteTarget, setDeleteTarget] = useState<GroupRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  /** 並び替え API 中はドラッグを無効化（二重送信防止） */
  const [reordering, setReordering] = useState(false);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchGroups = useCallback(async () => {
    setError('');
    try {
      const res = await apiClient.get<ManualGroupsTreeResponse>('/api/manual/groups');
      setGroups(res.data.groups);
    } catch {
      setError('一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups]);

  const openCreate = () => {
    setEditing(null);
    setNameInput('');
    setSortInput('0');
    setDialogOpen(true);
  };

  const openEdit = (g: GroupRow) => {
    setEditing(g);
    setNameInput(g.name);
    setDialogOpen(true);
  };

  const saveDialog = async () => {
    const name = nameInput.trim();
    if (!name || name.length > 200) {
      setError('名前は 1〜200 文字で入力してください');
      return;
    }
    if (!editing) {
      const sortOrder = Number.parseInt(sortInput, 10);
      if (!Number.isInteger(sortOrder)) {
        setError('表示順は整数で入力してください');
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await apiClient.patch(`/api/manual/groups/${editing.id}`, { name });
      } else {
        const sortOrder = Number.parseInt(sortInput, 10);
        await apiClient.post('/api/manual/groups', { name, sort_order: sortOrder });
      }
      setDialogOpen(false);
      await fetchGroups();
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      setError(msg || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (g: GroupRow) => {
    if (g.name === DEFAULT_MANUAL_GROUP_NAME) return;
    setDeleteTarget(g);
  };

  const closeDeleteConfirm = () => {
    if (!deleting) setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    setDeleting(true);
    try {
      await apiClient.delete(`/api/manual/groups/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchGroups();
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        setError(
          'このグループにはマニュアルが残っているため削除できません。マニュアルを別のグループへ移すか、マニュアルを削除してから再度お試しください。',
        );
      } else {
        const msg =
          axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
            ? (e.response.data as { error?: string }).error
            : null;
        setError(msg || '削除に失敗しました');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDialog = () => {
    if (!saving) setDialogOpen(false);
  };

  /** ドロップ後、0..n-1 の連番に正規化した sort_order を変更分だけ PATCH */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) return;

    const activeStr = String(active.id);
    const overStr = String(over.id);
    if (!activeStr.startsWith('group-') || !overStr.startsWith('group-')) return;

    const previousOrder = groupsRef.current;
    const oldIndex = previousOrder.findIndex((g) => `group-${g.id}` === activeStr);
    const newIndex = previousOrder.findIndex((g) => `group-${g.id}` === overStr);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(previousOrder, oldIndex, newIndex);
    const withNewSort = reordered.map((g: GroupRow, i: number) => ({ ...g, sort_order: i }));

    const toPatch = withNewSort.filter(
      (g: GroupRow) => previousOrder.find((p: GroupRow) => p.id === g.id)!.sort_order !== g.sort_order,
    );
    if (toPatch.length === 0) return;

    setGroups(withNewSort);
    setReordering(true);
    setError('');
    try {
      await Promise.all(
        toPatch.map((g: GroupRow) => apiClient.patch(`/api/manual/groups/${g.id}`, { sort_order: g.sort_order })),
      );
    } catch (e: unknown) {
      setGroups(previousOrder);
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      setError(msg || '表示順の保存に失敗しました');
    } finally {
      setReordering(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        マニュアルグループ管理
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate(BOARD_MANUAL_PATH)}>
          一覧へ
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          グループを追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          読み込み中…
        </Typography>
      ) : (
        <Paper>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e: DragEndEvent) => void handleDragEnd(e)}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell>名前</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <SortableContext items={groups.map((g) => `group-${g.id}`)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {groups.map((g) => (
                    <SortableGroupRow
                      key={g.id}
                      group={g}
                      disabled={reordering}
                      onEdit={openEdit}
                      onDelete={openDeleteConfirm}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </DndContext>
        </Paper>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>
          {editing ? 'グループを編集' : 'グループを追加'}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <TextField
            label="名前"
            fullWidth
            value={nameInput}
            disabled={editing?.name === DEFAULT_MANUAL_GROUP_NAME}
            onChange={(e) => setNameInput(e.target.value)}
            sx={{ mt: 1, mb: editing ? 0 : 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={handleCloseDialog} disabled={saving} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={() => void saveDialog()} disabled={saving} sx={{ minWidth: 100 }}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>グループを削除しますか？</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          {deleteTarget ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                グループ「{deleteTarget.name}」を削除します。この操作は取り消せません。
              </Typography>
              {deleteTarget.manuals.length > 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  このグループにはマニュアルが {deleteTarget.manuals.length} 件あります。削除するには、各マニュアルを別のグループへ移すか、マニュアル自体を削除してください。
                </Alert>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={closeDeleteConfirm} disabled={deleting} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmDelete()}
            disabled={deleting || (deleteTarget !== null && deleteTarget.manuals.length > 0)}
            sx={{ minWidth: 100 }}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualGroupsAdmin;
